/**
 * Sangria e suprimento — o MESMO formulário, com o tipo por parâmetro.
 *
 * São duas ações opostas e um único diálogo porque os campos são os mesmos e a
 * diferença cabe em três strings. Dois arquivos quase iguais seriam dois
 * lugares para corrigir quando o limite de valor mudar, e o segundo ficaria
 * para trás.
 *
 * O destino só aparece na sangria: suprimento é dinheiro ENTRANDO, e o
 * servidor recusa destino nele (422). A tela não mostra um campo que a API
 * rejeita.
 */
import { useState, type FormEvent } from 'react'

import { Botao } from '../../../componentes/Botao'
import { Campo, MensagemDeErro } from '../../../componentes/Campos'
import { Dialogo } from '../../../componentes/Dialogo'
import { analisar, ehValido, formatarMoeda, type Centavos } from '../../../plataforma/dinheiro'
import { lancarMovimento } from '../../../plataforma/caixa'
import { ErroApi } from '../../../plataforma/api'

type Tipo = 'sangria' | 'suprimento'

const TEXTOS: Record<Tipo, { titulo: string; descricao: string; acao: string }> = {
  sangria: {
    titulo: 'Sangria',
    descricao:
      'Retirada de dinheiro da gaveta. Diga para onde ele vai — é a primeira pergunta de qualquer conferência.',
    acao: 'Registrar sangria',
  },
  suprimento: {
    titulo: 'Suprimento',
    descricao: 'Entrada de dinheiro na gaveta, normalmente troco vindo do cofre.',
    acao: 'Registrar suprimento',
  },
}

export function DialogoMovimento({
  tipo,
  codigo,
  gaveta,
  aoFechar,
  aoLancar,
}: {
  tipo: Tipo
  codigo: string
  /** O que há na gaveta agora — usado só para avisar antes de o servidor recusar. */
  gaveta: Centavos
  aoFechar: () => void
  aoLancar: () => void
}) {
  const [valor, setValor] = useState('')
  const [destino, setDestino] = useState('')
  const [descricao, setDescricao] = useState('')
  const [erro, setErro] = useState('')
  const [enviando, setEnviando] = useState(false)

  const texto = TEXTOS[tipo]
  const centavos = ehValido(valor) ? analisar(valor) : null

  // Aviso, e não bloqueio: quem decide é o servidor, dentro da transação, sobre
  // os movimentos já gravados (a tela pode estar aberta há três vendas). Isto
  // aqui existe para a pessoa não descobrir o problema depois de digitar tudo.
  const passaDaGaveta = tipo === 'sangria' && centavos !== null && centavos > gaveta

  async function enviar(evento: FormEvent) {
    evento.preventDefault()
    if (enviando) return

    if (centavos === null || centavos <= 0) {
      setErro('Informe um valor maior que zero.')
      return
    }

    setEnviando(true)
    setErro('')
    try {
      await lancarMovimento(codigo, {
        tipo,
        valor_centavos: centavos,
        destino: tipo === 'sangria' ? destino.trim() : undefined,
        descricao: descricao.trim() || undefined,
      })
      aoLancar()
    } catch (falha) {
      setErro(falha instanceof ErroApi ? falha.message : 'Não foi possível registrar o movimento.')
      setEnviando(false)
    }
  }

  return (
    <Dialogo
      titulo={texto.titulo}
      icone={tipo === 'sangria' ? 'trending_down' : 'trending_up'}
      descricao={texto.descricao}
      aoFechar={aoFechar}
    >
      <form onSubmit={enviar} className="flex flex-col gap-4" aria-label={texto.titulo}>
        <Campo
          rotulo="Valor"
          inputMode="decimal"
          placeholder="0,00"
          value={valor}
          onChange={(e) => setValor(e.target.value)}
          erro={
            passaDaGaveta
              ? `A gaveta tem ${formatarMoeda(gaveta)} — não dá para sangrar mais que isso.`
              : undefined
          }
          aria-invalid={passaDaGaveta || undefined}
          dica={tipo === 'sangria' ? `Na gaveta agora: ${formatarMoeda(gaveta)}` : undefined}
          autoFocus
        />

        {tipo === 'sangria' && (
          <Campo
            rotulo="Destino"
            placeholder="Cofre, banco ou malote"
            value={destino}
            onChange={(e) => setDestino(e.target.value)}
            required
          />
        )}

        <Campo
          rotulo="Observação"
          placeholder="Opcional"
          value={descricao}
          onChange={(e) => setDescricao(e.target.value)}
        />

        {erro && <MensagemDeErro>{erro}</MensagemDeErro>}

        <div className="mt-1 flex justify-end gap-2.5">
          <Botao type="button" variante="secundario" porte="medio" onClick={aoFechar}>
            Cancelar
          </Botao>
          <Botao type="submit" porte="medio" disabled={enviando || passaDaGaveta}>
            {enviando ? 'Registrando…' : texto.acao}
          </Botao>
        </div>
      </form>
    </Dialogo>
  )
}
